import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { ClipboardList, FileText, QrCode } from "lucide-react";

const API = process.env.REACT_APP_API_URL;

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });

const dateText = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";

function StatCard({ title, value, helper, icon: Icon, tone, className = "" }) {
  return (
    <div
      className={`bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 min-w-0 ${className}`}
    >
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <div className="min-w-0">
          <p className="text-[11px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider leading-tight">
            {title}
          </p>
          <p className="mt-2 text-lg sm:text-2xl font-semibold text-gray-900 break-words leading-tight">
            {value}
          </p>
          {helper && (
            <p className="mt-1 text-[11px] sm:text-xs text-gray-500 leading-snug">
              {helper}
            </p>
          )}
        </div>
        <div
          className={`h-8 w-8 sm:h-10 sm:w-10 shrink-0 rounded-lg flex items-center justify-center ${tone}`}
        >
          <Icon size={18} strokeWidth={2.2} className="sm:h-5 sm:w-5" />
        </div>
      </div>
    </div>
  );
}

export default function MerchantDashboard() {
  const { userInfo } = useSelector((state) => state.userLogin || {});
  const [data, setData] = useState({
    summary: {},
    recent: [],
  });
  const [error, setError] = useState("");

  const merchantId = userInfo?.merchantId || "";
  const qrPath = userInfo?.merchantQrToken
    ? `/admin/merchants/${userInfo.merchantQrToken}`
    : "";
  const authConfig = {
    headers: userInfo?.token
      ? { Authorization: `Bearer ${userInfo.token}` }
      : {},
  };

  const fetchDashboard = async () => {
    if (!merchantId) return;
    setError("");
    try {
      const { data } = await axios.get(`${API}/report/merchant-dashboard`, {
        ...authConfig,
        params: { merchantId },
      });
      setData(data || {});
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Dashboard failed to load",
      );
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  const summary = data.summary || {};
  const walletBalance = Number(data.walletBalance || 0);
  const recentEnquiries = (data.recent || []).slice(0, 5);
  const activeCount = summary.active || 0;

  if (!merchantId) {
    return (
      <div className="-mx-3 px-2 py-3 sm:mx-0 sm:p-6 bg-gray-50 min-h-screen">
        <div className="bg-white border border-gray-200 rounded-xl p-6 max-w-lg">
          <h1 className="text-xl font-semibold text-gray-800 mb-2">
            Merchant dashboard unavailable
          </h1>
          <p className="text-sm text-gray-500">
            This login is not linked with a merchant account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="-mx-3 px-2 py-3 sm:mx-0 sm:p-5 bg-gray-50 min-h-screen w-[calc(100%+1.5rem)] sm:w-full">
      <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-start sm:justify-between w-full">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
            Merchant Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {userInfo?.name || userInfo?.merchantName || "Merchant"}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
          {qrPath && (
            <Link
              to={qrPath}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 text-sm font-medium px-4 py-2 rounded-lg no-underline"
            >
              <QrCode size={16} /> My QR
            </Link>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-5">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatCard
          title="Total Enquiries"
          value={summary.total || 0}
          icon={ClipboardList}
          tone="bg-blue-50 text-blue-600"
        />
        <StatCard
          title="Wallet Balance"
          value={`₹${money(walletBalance)}`}
          helper="Includes lead + FE login + FE disbursement"
          icon={FileText}
          tone="bg-emerald-50 text-emerald-600"
        />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden min-w-0">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700">
            Recent Enquiries
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                {[
                  "S.No",
                  "Case",
                  "Mobile",
                  "Loan Type",
                  "Status",
                  "Amount",
                  "Lead Date",
                  "Lead Amt",
                  "FE Login",
                  "FE Disburse",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recentEnquiries.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-400 text-sm">No enquiries found</td></tr>
              ) : recentEnquiries.map((row, index) => {
                const leadAmt = row.approved ? Number(row.approvedAmount || 50) : 0;
                const feLogin = Number(row.feLoginAmount) || 0;
                const feDisbursement = Number(row.feDisbursementAmount) || 0;
                return (
                  <tr key={row._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{index + 1}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap">{row.caseName || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{row.mobileNumber || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{row.loanType || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{row.status || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">₹{money(row.amountRequired)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{dateText(row.leadDate || row.createdAt)}</td>
                    <td className="px-4 py-3 text-sm text-blue-600 font-medium whitespace-nowrap">₹{money(leadAmt)}</td>
                    <td className="px-4 py-3 text-sm text-purple-600 font-medium whitespace-nowrap">₹{money(feLogin)}</td>
                    <td className="px-4 py-3 text-sm text-orange-600 font-medium whitespace-nowrap">₹{money(feDisbursement)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 font-semibold">
                <td colSpan={7} className="px-4 py-3 text-sm text-gray-700">Totals</td>
                <td className="px-4 py-3 text-sm text-gray-700">₹{money(summary.approvedLeadAmount || 0)}</td>
                <td className="px-4 py-3 text-sm text-gray-700">₹{money(summary.loginAmount || 0)}</td>
                <td className="px-4 py-3 text-sm text-gray-700">₹{money(summary.disbursementAmount || 0)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
